import { useCallback } from "react";
import type { ActionItem, FocusState, Project } from "../types";

interface UseKeyboardNavigationProps {
  projects: Project[];
  globalActionItems: ActionItem[];
  focusState: FocusState;
  setFocusState: React.Dispatch<React.SetStateAction<FocusState>>;
  onEscape: () => void;
  onEnter: () => void;
  onTab?: () => void;
}

export function useKeyboardNavigation({
  projects,
  globalActionItems,
  focusState,
  setFocusState,
  onEscape,
  onEnter,
  onTab,
}: UseKeyboardNavigationProps) {
  const currentProject =
    focusState.projectIndex >= 0 ? projects[focusState.projectIndex] : null;

  /*
   * GLOBAL KEYBOARD NAVIGATION SYSTEM
   * Handles j/k and arrow key navigation throughout the interface.
   * This system works alongside (not instead of) browser Tab navigation.
   *
   * IMPORTANT: This handler is bypassed for "Add New" cards to allow their
   * individual onKeyDown handlers to work for Enter key functionality.
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      /*
       * ESCAPE KEY: Universal back/cancel action
       * - In edit modes: Cancel editing without saving
       * - In action items mode: Return to project mode (keep project expanded)
       * - In global action items: Return to last project in projects mode
       * - In projects mode with expanded project: Collapse the expanded project
       */
      if (e.key === "Escape") {
        e.preventDefault();
        onEscape();
        return;
      }

      /*
       * SKIP KEYBOARD HANDLING IN INPUT FIELDS
       * Prevents navigation keys from interfering with typing (except Escape above)
       */
      if (
        (e.target as HTMLElement).tagName === "INPUT" ||
        (e.target as HTMLElement).tagName === "TEXTAREA"
      ) {
        return;
      }

      /*
       * SKIP KEYBOARD HANDLING ON "ADD NEW" CARDS
       * Allows individual onKeyDown handlers on these cards to work for Enter key.
       * This prevents conflict between global navigation and card-specific actions.
       */
      const target = e.target as HTMLElement;
      if (
        target.hasAttribute("data-new-project") ||
        target.hasAttribute("data-new-action-item")
      ) {
        return;
      }

      switch (e.key) {
        /*
         * ARROW DOWN / J KEY: Navigate down through the interface
         * Behavior depends on current mode and position:
         */
        case "ArrowDown":
        case "j":
          e.preventDefault();

          /*
           * PROJECTS MODE: Navigate through projects and transition to action items
           */
          if (focusState.mode === "projects") {
            if (focusState.projectIndex === -1) {
              // Move from "Add New Project" card to first real project
              setFocusState((prev) => ({
                ...prev,
                projectIndex: 0,
              }));
            } else if (
              currentProject &&
              focusState.expandedProjectId === currentProject.id
            ) {
              // Current project is expanded, dive into its action items
              // Start with "Add New Action Item" card (index -1)
              setFocusState((prev) => ({
                ...prev,
                mode: "actionItems",
                actionItemIndex: -1,
              }));
            } else if (focusState.projectIndex === projects.length - 1) {
              // At last project, transition to global action items section
              // Start with "Add New Global Action Item" card (index -1)
              setFocusState((prev) => ({
                ...prev,
                mode: "globalActionItems",
                actionItemIndex: -1,
              }));
            } else {
              // Move to next project in the list
              setFocusState((prev) => ({
                ...prev,
                projectIndex: Math.min(
                  prev.projectIndex + 1,
                  projects.length - 1,
                ),
              }));
            }

            /*
             * ACTION ITEMS MODE: Navigate through action items within a project
             */
          } else if (focusState.mode === "actionItems" && currentProject) {
            if (focusState.actionItemIndex === -1) {
              // Move from "Add New Action Item" to first real action item
              setFocusState((prev) => ({
                ...prev,
                actionItemIndex: 0,
              }));
            } else if (
              currentProject &&
              focusState.actionItemIndex ===
                currentProject.actionItems.length - 1
            ) {
              // At last action item of current project
              // Try to move to next project and auto-expand it
              const nextProjectIndex = Math.min(
                focusState.projectIndex + 1,
                projects.length - 1,
              );
              if (nextProjectIndex !== focusState.projectIndex) {
                // Next project exists, switch to it and expand
                const nextProject = projects[nextProjectIndex];
                setFocusState((prev) => ({
                  ...prev,
                  mode: "projects",
                  projectIndex: nextProjectIndex,
                  expandedProjectId: nextProject.id,
                }));
              }
              // If already at last project, stay on current action item
            } else {
              // Move to next action item in current project
              setFocusState((prev) => ({
                ...prev,
                actionItemIndex: prev.actionItemIndex + 1,
              }));
            }

            /*
             * GLOBAL ACTION ITEMS MODE: Navigate through global action items
             */
          } else if (focusState.mode === "globalActionItems") {
            if (focusState.actionItemIndex === -1) {
              // Move from "Add New Global Action Item" to first real item
              setFocusState((prev) => ({
                ...prev,
                actionItemIndex: 0,
              }));
            } else {
              // Move to next global action item (or stay at last)
              setFocusState((prev) => ({
                ...prev,
                actionItemIndex: Math.min(
                  prev.actionItemIndex + 1,
                  globalActionItems.length - 1,
                ),
              }));
            }
          }
          break;

        /*
         * ARROW UP / K KEY: Navigate up through the interface
         * Essentially the reverse of down navigation:
         */
        case "ArrowUp":
        case "k":
          e.preventDefault();

          /*
           * PROJECTS MODE: Navigate up through projects
           */
          if (focusState.mode === "projects") {
            if (focusState.projectIndex === 0) {
              // Move from first project to "Add New Project" card
              setFocusState((prev) => ({
                ...prev,
                projectIndex: -1,
              }));
            } else {
              // Move to previous project (or to "Add New Project" if at first)
              setFocusState((prev) => ({
                ...prev,
                projectIndex: Math.max(prev.projectIndex - 1, -1),
              }));
            }

            /*
             * ACTION ITEMS MODE: Navigate up through action items or exit to project
             */
          } else if (focusState.mode === "actionItems") {
            if (focusState.actionItemIndex === -1) {
              // Exit action items mode, return to project focus (keep expanded)
              setFocusState((prev) => ({
                ...prev,
                mode: "projects",
              }));
            } else if (focusState.actionItemIndex === 0) {
              // Move from first action item to "Add New Action Item" card
              setFocusState((prev) => ({
                ...prev,
                actionItemIndex: -1,
              }));
            } else {
              // Move to previous action item
              setFocusState((prev) => ({
                ...prev,
                actionItemIndex: Math.max(prev.actionItemIndex - 1, 0),
              }));
            }

            /*
             * GLOBAL ACTION ITEMS MODE: Navigate up through global action items or exit
             */
          } else if (focusState.mode === "globalActionItems") {
            if (focusState.actionItemIndex === -1) {
              // Exit global action items, return to last project in projects mode
              setFocusState((prev) => ({
                ...prev,
                mode: "projects",
                projectIndex: projects.length - 1,
              }));
            } else if (focusState.actionItemIndex === 0) {
              // Move from first global action item to "Add New Global Action Item"
              setFocusState((prev) => ({
                ...prev,
                actionItemIndex: -1,
              }));
            } else {
              // Move to previous global action item
              setFocusState((prev) => ({
                ...prev,
                actionItemIndex: Math.max(prev.actionItemIndex - 1, 0),
              }));
            }
          }
          break;

        /*
         * ENTER KEY: Context-dependent action
         * - On projects: Expand (if collapsed) or edit (if expanded)
         * - On action items: Enter edit mode
         * - On "Add New" cards: Handled by individual onKeyDown handlers (not here)
         */
        case "Enter":
          e.preventDefault();
          onEnter();
          break;

        /*
         * TAB KEY: Field navigation within edit modes
         * Only active when in edit modes, otherwise allows normal Tab behavior
         */
        case "Tab":
          if (
            onTab &&
            (focusState.mode === "editProject" ||
              focusState.mode === "editActionItem")
          ) {
            e.preventDefault();
            onTab();
          }
          // If not in edit mode, allow normal Tab behavior for browser focus
          break;
      }
    },
    [
      focusState,
      currentProject,
      projects,
      globalActionItems.length,
      onEscape,
      onEnter,
      onTab,
      setFocusState,
    ],
  );

  return {
    handleKeyDown,
  };
}
